import Tree from "rc-tree";
import "rc-tree/assets/index.css";
import { useEffect, useState } from "react";
import { Control, useController } from "react-hook-form";
import ProfileService from "../../../services/ProfileService";

type PermissionTreeProps = {
  control: Control<any>;
};

type PermissionNode = {
  id: number;
  name: string;
  displayName: string;
};

const mapPermissionNode = (
  permission: PermissionNode,
  disableCalendarEdit: boolean
) => ({
  title: permission.displayName,
  key: `${permission.id}`,
  isLeaf: true,
  disableCheckbox:
    disableCalendarEdit && permission.name === "calendar_edit"
});

const permissionCategories = [
  { title: "Entidades", matches: (permission: PermissionNode) => permission.name.startsWith("entity") },
  { title: "Robôs", matches: (permission: PermissionNode) => permission.name.startsWith("bot") },
  { title: "Colaboradores", matches: (permission: PermissionNode) => permission.name.startsWith("user") },
  { title: "Workspaces", matches: (permission: PermissionNode) => permission.name.startsWith("workspace") },
  { title: "Perfis", matches: (permission: PermissionNode) => permission.name.startsWith("profile") },
  { title: "Gestão de salas", matches: (permission: PermissionNode) => permission.name.startsWith("dialog") },
  { title: "Credenciais", matches: (permission: PermissionNode) => permission.name.startsWith("credential") },
  { title: "Dashboard", matches: (permission: PermissionNode) => permission.name.startsWith("dashboard") },
  { title: "Omnichannel", matches: (permission: PermissionNode) => permission.name.startsWith("omnichannel") },
  { title: "Templates", matches: (permission: PermissionNode) => permission.name.startsWith("template") },
  {
    title: "Contatos",
    matches: (permission: PermissionNode) =>
      permission.name.startsWith("contact") &&
      !permission.name.startsWith("contactPipeline")
  },
  { title: "Campanha", matches: (permission: PermissionNode) => permission.name.startsWith("campaign") },
  { title: "Exclusão de mensagens", matches: (permission: PermissionNode) => permission.name.startsWith("message") },
  { title: "Pipelines", matches: (permission: PermissionNode) => permission.name.startsWith("pipeline") },
  { title: "Contatos do pipeline", matches: (permission: PermissionNode) => permission.name.startsWith("contactPipeline") },
  { title: "Calendar", matches: (permission: PermissionNode) => permission.name.startsWith("calendar_") }
];

export default ({ control }: PermissionTreeProps) => {
  const [permissions, setPermissions] = useState<PermissionNode[]>([]);
  const { field } = useController({
    name: "permissions",
    control
  });

  const { findPermissions } = ProfileService();

  useEffect(() => {
    const run = async () => {
      const permissions = await findPermissions();

      setPermissions(permissions);
    };

    run();
  }, []);

  const calendarIncludePermission = permissions.find(
    (permission) => permission.name === "calendar_include"
  );
  const calendarEditPermission = permissions.find(
    (permission) => permission.name === "calendar_edit"
  );
  const validPermissionKeys = permissions.map((permission) => `${permission.id}`);

  const normalizeCheckedKeys = (value: any): string[] => {
    if (Array.isArray(value)) {
      return value.map(String);
    }

    if (value?.checked && Array.isArray(value.checked)) {
      return value.checked.map(String);
    }

    return [];
  };

  const syncCalendarPermissions = (checkedKeys: string[]) => {
    if (!calendarIncludePermission || !calendarEditPermission) {
      return checkedKeys;
    }

    if (checkedKeys.includes(`${calendarIncludePermission.id}`)) {
      return Array.from(
        new Set([...checkedKeys, `${calendarEditPermission.id}`])
      );
    }

    return checkedKeys;
  };

  const buildTreeData = (disableCalendarEdit: boolean) => {
    let key = 0;

    return permissionCategories.map((category) => ({
      title: category.title,
      key: key--,
      children: permissions
        .filter(category.matches)
        .map((permission) =>
          mapPermissionNode(permission, disableCalendarEdit)
        )
    }));
  };

  useEffect(() => {
    if (!calendarIncludePermission || !calendarEditPermission) {
      return;
    }

    const checkedKeys = normalizeCheckedKeys(field.value);
    const isCalendarIncludeChecked = checkedKeys.includes(
      `${calendarIncludePermission.id}`
    );
    const isCalendarEditChecked = checkedKeys.includes(
      `${calendarEditPermission.id}`
    );

    if (isCalendarIncludeChecked && !isCalendarEditChecked) {
      field.onChange([
        ...checkedKeys.map(Number),
        calendarEditPermission.id
      ]);
    }
  }, [
    calendarEditPermission,
    calendarIncludePermission,
    field,
    field.value
  ]);

  const checkedKeys = syncCalendarPermissions(
    normalizeCheckedKeys(field.value).filter((key) =>
      validPermissionKeys.includes(key)
    )
  );
  const isCalendarIncludeChecked =
    !!calendarIncludePermission &&
    checkedKeys.includes(`${calendarIncludePermission.id}`);
  const treeData = buildTreeData(isCalendarIncludeChecked);

  return (
    <Tree
      checkable={true}
      onCheck={(checked) =>
        field.onChange(
          syncCalendarPermissions(normalizeCheckedKeys(checked)).map(Number)
        )
      }
      checkedKeys={checkedKeys}
      treeData={treeData}
      showIcon={false}
    />
  );
};
